import React, { useState, useEffect } from 'react';
import { Copy, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import Header from '../Header';
import { toast } from 'sonner';
import { getMeets, deleteMeet } from '../../utils/api';
import { getSportLabel, normalizeSportType } from '../../utils/sportConfig';

// Per-sport accent colors for the inline "Sport" badge on each recent
// meet card. Track stays blue (the legacy color), Swim picks up cyan,
// XC picks up emerald. Light backgrounds + bold borders so the badge
// reads at a glance against the white meet card.
const SPORT_BADGE_STYLES = {
  track: 'bg-blue-50 text-blue-700 border-blue-200',
  swim: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  xc: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export default function SelectTemplate({ onNext, onSelect, onBack }) {
  const [meets, setMeets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, meetId: null, meetName: '' });

  // Load meets from API and localStorage on mount
  useEffect(() => {
    const loadMeets = async () => {
      setIsLoading(true);
      try {
        // getMeets() returns an array (with cloud as the source of truth and
        // any offline-only local meets merged in). It also prunes localStorage
        // of any meet that was deleted on the cloud, so we use its return
        // value directly — do NOT re-read localStorage here, or we'll
        // resurrect just-deleted meets.
        const allMeets = await getMeets();

        // Sort by created date (most recent first)
        const sorted = [...allMeets].sort(
          (a, b) => new Date(b.created || 0) - new Date(a.created || 0)
        );

        // Dedupe by name - keep only the most recent meet for each unique name
        const uniqueByName = new Map();
        sorted.forEach(meet => {
          if (!uniqueByName.has(meet.name)) {
            uniqueByName.set(meet.name, meet);
          }
        });

        // Take top 10 unique meets
        setMeets(Array.from(uniqueByName.values()).slice(0, 10));
      } catch (error) {
        console.error('Error loading meets:', error);
        // Fallback to localStorage only
        const allMeets = JSON.parse(localStorage.getItem('foresight_meets') || '[]');
        const uniqueByName = new Map();
        allMeets.reverse().forEach(meet => {
          if (!uniqueByName.has(meet.name)) {
            uniqueByName.set(meet.name, meet);
          }
        });
        setMeets(Array.from(uniqueByName.values()).slice(0, 10));
      } finally {
        setIsLoading(false);
      }
    };

    loadMeets();
  }, []);

  const handleUseTemplate = (meet) => {
    // Pass the full meet data but clear date, time, venue so user must enter new ones
    const templateData = {
      ...meet,
      // Keep these from template:
      type: meet.type,
      level: meet.level,
      // sport_type / pool_lanes inherit too — re-using a swim meet
      // template should land the wizard in swim mode with the same
      // pool size pre-filled.
      sport_type: meet.sport_type || 'track',
      pool_lanes: meet.pool_lanes ?? null,
      pool_unit: meet.pool_unit ?? null,
      swim_preset: meet.swim_preset ?? null,
      events: meet.events,
      // Clear these so user enters new values:
      name: '', // Clear name so user can enter new meet name
      date: '', // Must be cleared - can't use old date
      startTime: '', // Must be cleared
      venue: '', // Clear so user can choose new venue
      venueAddress: '',
      venuePlaceId: '',
      // Mark as template
      isFromTemplate: true,
      templateSourceId: meet.id,
      templateSourceName: meet.name,
    };
    onSelect(templateData);
    onNext();
  };

  const handleStartFresh = () => {
    onSelect(null);
    onNext();
  };

  const handleDeleteClick = (meetId, meetName, e) => {
    e.stopPropagation(); // Prevent card click
    setDeleteDialog({ open: true, meetId, meetName });
  };

  const handleDeleteConfirm = async () => {
    const { meetId } = deleteDialog;
    setDeleteDialog({ open: false, meetId: null, meetName: '' });

    try {
      // Delete from cloud (and localStorage). Without the cloud delete,
      // the meet would just reappear next time getMeets() runs.
      await deleteMeet(meetId);
      setMeets(prev => prev.filter(m => String(m.id) !== String(meetId)));
      toast.success('Meet deleted');
    } catch (error) {
      console.error('Failed to delete meet:', error);
      toast.error('Failed to delete meet');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header showBack={true} onBack={onBack} title="Create New Meet" />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Use Recent Meet */}
          <section>
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-foreground">Use a Recent Meet as Template</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Copy meet type, level, and events. You&apos;ll enter a new date, time, and venue.
              </p>
            </div>

            <div className="grid gap-4">
              {isLoading ? (
                <Card className="p-6 text-center text-muted-foreground">
                  <p>Loading recent meets...</p>
                </Card>
              ) : meets.length > 0 ? (
                meets.map(meet => {
                  const sport = normalizeSportType(meet.sport_type);
                  return (
                  <Card 
                    key={meet.id}
                    className="p-6 hover:shadow-elegant transition-smooth cursor-pointer group"
                    onClick={() => handleUseTemplate(meet)}
                    data-testid={`recent-meet-card-${meet.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0 pr-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-lg text-foreground group-hover:text-primary transition-smooth truncate">
                            {meet.name}
                          </h3>
                          <Badge
                            variant="outline"
                            className={`text-[10px] px-1.5 py-0 h-5 flex-shrink-0 ${SPORT_BADGE_STYLES[sport]}`}
                            data-testid={`recent-meet-sport-badge-${meet.id}`}
                          >
                            {getSportLabel(sport)}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1" data-testid={`template-meet-meta-${meet.id}`}>
                          {meet.venue} · {meet.events?.length || 0} events
                          {/* XC is exclusively outdoor — the chip
                              would be tautological, so skip it for
                              that sport while keeping it for Track /
                              Swim. */}
                          {meet.sport_type !== 'xc' && ` · ${meet.type}`}
                          {' · '}{meet.level}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="flex items-center gap-2 text-muted-foreground group-hover:text-primary transition-smooth">
                          <Copy className="w-5 h-5" />
                          <span className="text-sm font-medium hidden sm:inline">Use Template</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          onClick={(e) => handleDeleteClick(meet.id, meet.name, e)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                  );
                })
              ) : (
                <Card className="p-6 text-center text-muted-foreground">
                  <p>No recent meets found. Start fresh to create your first meet.</p>
                </Card>
              )}
            </div>
          </section>

          {/* Start Fresh */}
          <section>
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-foreground">Start Fresh (No Template)</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Build a new meet from scratch with full customization.
              </p>
            </div>

            <Card 
              className="p-8 hover:shadow-elegant transition-smooth cursor-pointer group border-2 border-dashed"
              onClick={handleStartFresh}
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-foreground group-hover:text-primary transition-smooth">
                    Create New Meet
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Start with a blank slate and customize everything
                  </p>
                </div>
              </div>
            </Card>
          </section>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Delete Meet
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteDialog.meetName}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialog({ open: false, meetId: null, meetName: '' })}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteConfirm}
              className="flex-1"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
